import { CalendarClock, Clock3, Droplets, Zap } from "lucide-react";
import type { ReactNode } from "react";
import type { IrrigationPlanHistoryTimelineEntry } from "@/store/data";
import { dayjs, formatDurationLabel } from "@/utils/date";

type StatusVariant = "default" | "success" | "warning" | "info";

function StatusBadge({ children, variant = "default" }: { children: ReactNode; variant?: StatusVariant }) {
  const classes: Record<StatusVariant, string> = {
    default: "bg-gray-100 text-gray-700 border-gray-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${classes[variant]}`}
    >
      {children}
    </span>
  );
}

type StepTone = "neutral" | "ferti";

function StepBadge({ label, tone = "neutral" }: { label: string; tone?: StepTone }) {
  const toneClasses: Record<StepTone, string> = {
    neutral: "bg-gray-100 text-gray-700 border-gray-200",
    ferti: "bg-purple-50 text-purple-700 border-purple-200",
  };

  return (
    <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${toneClasses[tone]}`}>
      {label}
    </span>
  );
}

export function TimelineEntryCard({ entry }: { entry: IrrigationPlanHistoryTimelineEntry }) {
  const startLabel = dayjs(entry.startTime).format("DD/MM/YYYY HH:mm");
  const endLabel = entry.endTime ? dayjs(entry.endTime).format("DD/MM/YYYY HH:mm") : "Em andamento";
  const packetRelative = dayjs(entry.packetDate).fromNow();

  const planLabel = entry.planName ?? "Plano sem nome";
  const projectLabel = entry.projectName ?? "Projeto não identificado";

  const durationLabel = formatDurationLabel(entry.durationSeconds);

  const activatedSteps = entry.activatedSteps.length ? entry.activatedSteps : null;
  const fertiSteps = entry.activatedFertiSteps.length ? entry.activatedFertiSteps : null;

  return (
    <div className="relative pl-10">
      <span className="absolute left-3 top-4 inline-flex h-3 w-3 -translate-x-1/2 rounded-full border border-white bg-blue-500 shadow-sm" />
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900">{planLabel}</h3>
            <p className="text-sm text-gray-500">{projectLabel}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {entry.isOngoing ? (
              <StatusBadge variant="warning">Em andamento</StatusBadge>
            ) : (
              <StatusBadge variant="success">Concluído</StatusBadge>
            )}
            {entry.endTimeInferred && !entry.isOngoing ? (
              <StatusBadge variant="info">Encerramento inferido</StatusBadge>
            ) : null}
            {entry.usesWaterPump ? <StatusBadge variant="info">Bomba utilizada</StatusBadge> : null}
            {entry.usesFerti ? <StatusBadge variant="info">Fertirrigação</StatusBadge> : null}
            {entry.waterpumpWorking && entry.usesWaterPump ? (
              <StatusBadge variant="success">Bomba em operação</StatusBadge>
            ) : null}
            {!entry.waterpumpWorking && entry.usesWaterPump ? (
              <StatusBadge variant="default">Bomba inativa</StatusBadge>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="flex items-start gap-3">
            <CalendarClock className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Início</p>
              <p className="text-sm text-gray-600">{startLabel}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock3 className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Encerramento</p>
              <p className="text-sm text-gray-600">{endLabel}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Zap className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Duração</p>
              <p className="text-sm text-gray-600">{durationLabel}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Setores ativados</p>
            {activatedSteps ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {activatedSteps.map((step) => (
                  <StepBadge
                    key={step.id}
                    label={step.sectorName ?? step.description ?? `Passo ${step.order ?? "?"}`}
                  />
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">Nenhum setor foi ativado neste registro.</p>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Fertirrigação</p>
            {fertiSteps ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {fertiSteps.map((step) => (
                  <StepBadge
                    key={`ferti-${step.id}`}
                    tone="ferti"
                    label={step.sectorName ?? step.description ?? `Passo ${step.order ?? "?"}`}
                  />
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">Fertirrigação não foi acionada.</p>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <Droplets className="h-4 w-4" />
            <span>Registro atualizado {packetRelative}</span>
          </div>
          <span className="font-medium text-gray-400">ID #{entry.id}</span>
        </div>
      </div>
    </div>
  );
}

export default TimelineEntryCard;
